import React from "react";
import { AlertTriangle } from "lucide-react";
import { motion } from "framer-motion";

const CancelOrderModal = ({ 
  onClose, 
  onConfirm, 
  orderDetails
}) => {
  const formatDate = (dateString) => {
    if (!dateString) return "-";
    const date = new Date(dateString);
    return `${(date.getMonth() + 1).toString().padStart(2, "0")}-${date
      .getDate()
      .toString()
      .padStart(2, "0")}-${date.getFullYear()}`;
  };

  const handleConfirm = () => {
    onConfirm?.();
  };

  const isAlreadyCancelled = orderDetails?.status?.toUpperCase() === "CANCELLED" || 
                            orderDetails?.status?.toUpperCase() === "CANCELED";

  // Extract pickup and delivery dates from stops
  const pickupStop = orderDetails?.stops?.find(
    (s) => s.stopType === "PICKUP" || s.stopType === "PICK_UP"
  );
  const deliveryStop = orderDetails?.stops?.find(
    (s) => s.stopType === "DROP_OFF" || s.stopType === "DROPOFF"
  );

  return (
    <motion.div
      className="fixed inset-0 z-[1000] flex items-center justify-center bg-black bg-opacity-30 px-4"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
    >
      <motion.div
        className="bg-white rounded-2xl shadow-xl w-full max-w-3xl p-6 space-y-6"
        initial={{ scale: 0.95 }}
        animate={{ scale: 1 }}
        exit={{ scale: 0.95 }}
      >
        {/* Header */}
        <div className="flex flex-col items-center space-y-3">
          <div className="bg-red-600 rounded-full p-3 text-white">
            <AlertTriangle size={32} />
          </div>
          <h2 className="text-2xl font-semibold text-gray-900">
            Cancel Order
          </h2>
        </div>

      {/* Warning Banner */}
      <div className="bg-red-50 border-l-4 border-red-600 px-4 py-3 rounded-md space-y-2">
        <div className="flex items-center text-gray-900 space-x-2">
          <AlertTriangle className="text-gray-900" size={18} />
          <div className="text-base">
            <span className="font-bold">Warning</span>
            <span className="ml-1 font-medium">
              {isAlreadyCancelled
                ? "This order is already cancelled."
                : "Are you sure you want to cancel this order?"}
            </span>
          </div>
        </div>

        {!isAlreadyCancelled && (
          <p className="text-gray-900 text-sm pl-7">
            This action cannot be undone. The order will be permanently cancelled.
          </p>
        )}
      </div>

        {/* Order Info Grid */}
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 grid grid-cols-1 sm:grid-cols-2 gap-6 text-sm text-gray-800">
          {/* Left Section */}
          <div>
            <h4 className="font-bold mb-2">Order Information</h4>
            <div className="space-y-1">
              <div><span className="font-medium">Order Number:</span> {orderDetails?.orderNumber || "-"}</div>
              <div><span className="font-medium">Office Code:</span> {orderDetails?.officeCode || "-"}</div>
              <div><span className="font-medium">Status:</span> {orderDetails?.status || "-"}</div>
              <div><span className="font-medium">PO Number:</span> {orderDetails?.poNumber || "-"}</div>
              <div><span className="font-medium">Payment Method:</span> {orderDetails?.paymentMethod || "-"}</div>
            </div>
          </div>

          {/* Right Section */}
          <div>
            <h4 className="font-bold mb-2">Routing Details</h4>
            <div className="space-y-1">
              <div><span className="font-medium">Origin:</span> {orderDetails?.origin || "-"}</div>
              <div><span className="font-medium">Destination:</span> {orderDetails?.destination || "-"}</div>
              <div><span className="font-medium">Pickup Date:</span> {formatDate(pickupStop?.scheduledGateOut)}</div>
              <div><span className="font-medium">Delivery Date:</span> {formatDate(deliveryStop?.scheduledGateIn)}</div>
              <div><span className="font-medium">Requested Mode:</span> {orderDetails?.requestedMode || "-"}</div>
            </div>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex justify-end gap-3 pt-4 border-t border-gray-200">
          <button
            onClick={onClose}
            className="px-4 py-2 border border-gray-300 rounded text-gray-700 hover:bg-gray-100 text-sm transition"
          >
            Cancel
          </button>
          <button
            onClick={handleConfirm}
            disabled={isAlreadyCancelled}
            className={`px-4 py-2 text-white text-sm rounded font-semibold transition
              ${isAlreadyCancelled ? "bg-gray-400 cursor-not-allowed" : "bg-red-600 hover:bg-red-700"}`}
          >
            Cancel Order
          </button>
        </div>
      </motion.div>
    </motion.div>
  );
};

export default CancelOrderModal;